/**
 * The workflow canvas's links, drawn as AmoregGTM's connections:
 * right-angled routes with rounded corners, each a gradient from its source
 * step's accent to its target's; a Condition's links carry a Yes or No pill;
 * an outlet with no link yet is a stub ending in an endpoint to drag a link
 * from (while editing). On a run, the links it travelled are lit, the one
 * into a running step marches, and the rest stay faint.
 *
 * @module components/workflows/canvas/edges
 */
import type { WorkflowGraph, WorkflowNodeType } from "@amoreg/contracts";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { type CSSProperties, Fragment, memo, type PointerEvent as ReactPointerEvent } from "react";

import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import {
  actionPoint,
  labelPoint,
  nodeHeight,
  openPorts,
  outputPoint,
  type Point,
  type PortId,
  portOfEdge,
  roundedPath,
  routeEdges,
  routePoints,
} from "./geometry";
import { ChevronDownSmallIcon } from "./icons";
import { kindClass } from "./kinds";

export interface EdgePreview {
  readonly from: Point;
  readonly to: Point;
  readonly fromKind: WorkflowNodeType;
  readonly toKind: WorkflowNodeType | null;
}

/** How a link is drawn on a run. */
export type EdgeRunState = "travelled" | "flowing" | "skipped";

export const portKey = (source: string, port: PortId) => `${source}:${port}`;

const position = (point: Point) => ({ "--point-x": `${point.x}px`, "--point-y": `${point.y}px` }) as CSSProperties;

const PORT_WORD: Readonly<Record<PortId, string | null>> = { out: null, yes: "Yes", no: "No" };

/** GTM's branch pill, in the Condition's accent. */
function BranchLabel({ point, text }: { readonly point: Point; readonly text: string }) {
  return (
    <span
      style={position(point)}
      className={cn(
        kindClass("condition"),
        "pointer-events-none absolute [top:var(--point-y)] [left:var(--point-x)] flex h-5 -translate-x-1/2 -translate-y-1/2 items-center overflow-clip rounded-[21px] bg-kind/15 bg-[linear-gradient(178deg,var(--pill-sheen))] px-[9px] text-[12px] leading-6 font-semibold whitespace-nowrap text-kind uppercase backdrop-blur-[4px]",
      )}
      data-workflow-edge-label={text}
    >
      {text}
    </span>
  );
}

/** A path stroked with its own gradient; each stop reads its kind's accent. */
function GradientPath({
  id,
  from,
  to,
  fromKind,
  toKind,
  path,
  className,
  dashed,
  width = 2,
}: {
  readonly id: string;
  readonly from: Point;
  readonly to: Point;
  readonly fromKind: WorkflowNodeType;
  readonly toKind: WorkflowNodeType | "END";
  readonly path: string;
  readonly className?: string;
  readonly dashed?: boolean;
  readonly width?: number;
}) {
  const degenerate = Math.abs(from.x - to.x) < 0.5 && Math.abs(from.y - to.y) < 0.5;
  return (
    <>
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={from.x} y1={from.y} x2={degenerate ? from.x : to.x} y2={degenerate ? from.y + 1 : to.y}>
          <stop className={kindClass(fromKind)} style={{ stopColor: "var(--kind)" }} />
          <stop offset="1" className={toKind === "END" ? undefined : kindClass(toKind)} style={{ stopColor: toKind === "END" ? "var(--stub-end)" : "var(--kind)" }} />
        </linearGradient>
      </defs>
      <path
        d={path}
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth={width}
        strokeLinecap="round"
        {...(dashed ? { strokeDasharray: "6 6" } : {})}
        className={className}
      />
    </>
  );
}

export interface EdgeCues {
  readonly travelling: ReadonlyMap<string, string>;
  readonly moving: ReadonlySet<string>;
}

const GLIDE = "[transition:d_400ms_var(--ease-power3-out),opacity_150ms_var(--ease-power3-in-out)]";

/** SVG ids must be unique on the page and free of characters a url(#…) can't carry. */
const svgId = (prefix: string, raw: string) => `${prefix}-${raw.replace(/[^A-Za-z0-9_-]/g, "_")}`;

export const CanvasEdges = memo(function CanvasEdges({
  graph,
  sizes,
  selectedEdgeId,
  snapKey,
  preview,
  runStates,
  editable,
  insertOptions = false,
  insertEdgeId = null,
  cues = null,
  onEdgePointerDown,
  onEndpointPointerDown,
  onDeleteEdge,
}: {
  readonly graph: Pick<WorkflowGraph, "nodes" | "edges">;
  readonly sizes: Readonly<Record<string, number>>;
  readonly selectedEdgeId: string | null;
  readonly snapKey: string | null;
  readonly preview: EdgePreview | null;
  /** On a run, how each link is drawn; null while editing. */
  readonly runStates: ReadonlyMap<string, EdgeRunState> | null;
  readonly editable: boolean;
  /** A step is being brought out of the Steps panel: each link shows where it could be dropped in between. */
  readonly insertOptions?: boolean | undefined;
  /** The link the step would be dropped into, if the pointer is on its marker. */
  readonly insertEdgeId?: string | null | undefined;
  readonly cues?: EdgeCues | null | undefined;
  readonly onEdgePointerDown: (id: string, event: ReactPointerEvent) => void;
  readonly onEndpointPointerDown: (source: string, port: PortId, event: ReactPointerEvent) => void;
  readonly onDeleteEdge: () => void;
}) {
  const routed = routeEdges(graph, sizes);

  const labels: Array<{ readonly id: string; readonly point: Point; readonly text: string }> = [];
  for (const { edge, points } of routed) {
    const text = PORT_WORD[portOfEdge(edge)];
    if (!text) continue;
    const preferred = labelPoint(points);
    const taken = labels.some((label) => Math.abs(label.point.x - preferred.x) < 80 && Math.abs(label.point.y - preferred.y) < 24);
    labels.push({ id: edge.id, text, point: taken ? actionPoint(points) : preferred });
  }
  const open = editable ? openPorts(graph, sizes) : [];
  const selected = selectedEdgeId ? routed.find((item) => item.edge.id === selectedEdgeId) : undefined;

  return (
    <>
      <svg aria-hidden width="1" height="1" className="pointer-events-none absolute top-0 left-0 overflow-visible">
        {routed.map(({ edge, source, target, from, to, path }) => {
          const isSelected = edge.id === selectedEdgeId || edge.id === insertEdgeId;
          const run = runStates?.get(edge.id);
          const lit = isSelected || run === "travelled" || run === "flowing";
          const travelling = cues?.travelling.get(edge.id);
          const glides = travelling !== undefined || Boolean(cues?.moving.has(edge.source) || cues?.moving.has(edge.target));
          const elementOf = travelling ?? edge.id;
          return (
            <g key={elementOf} className="group/edge" data-edge={edge.id} data-workflow-edge={edge.id} data-lit={lit || undefined} data-run={run}>
              <GradientPath
                id={svgId("edge", edge.id)}
                from={from}
                to={to}
                fromKind={source.type}
                toKind={target.type}
                path={path}
                dashed={run === "flowing" || run === "skipped"}
                width={isSelected ? 3 : 2}
                className={cn(
                  glides ? GLIDE : "transition-opacity duration-150 ease-power3-in-out",
                  run === "flowing" && "animate-canvas-march",
                  lit ? "opacity-100" : run === "skipped" ? "opacity-15" : runStates ? "opacity-25" : "opacity-40 group-hover/edge:opacity-70",
                )}
              />
              {editable ? (
                <path
                  d={path}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={16}
                  onPointerDown={(event) => onEdgePointerDown(edge.id, event)}
                  className="cursor-pointer [pointer-events:stroke]"
                />
              ) : null}
            </g>
          );
        })}
        {open.map(({ source, port, point }) => {
          if (snapKey === portKey(source.id, port)) return null;
          const from = outputPoint(source, nodeHeight(source, sizes));
          return (
            <GradientPath
              key={portKey(source.id, port)}
              id={svgId("stub", `${source.id}-${port}`)}
              from={from}
              to={point}
              fromKind={source.type}
              toKind="END"
              path={roundedPath(routePoints(from, point))}
              className="opacity-40"
            />
          );
        })}
        {preview ? (
          <GradientPath
            id="edge-preview"
            from={preview.from}
            to={preview.to}
            fromKind={preview.fromKind}
            toKind={preview.toKind ?? "END"}
            path={roundedPath(routePoints(preview.from, preview.to))}
            className="opacity-70"
          />
        ) : null}
      </svg>

      {labels.map(({ id, point, text }) => (
        <BranchLabel key={id} point={point} text={text} />
      ))}

      {open.map(({ source, port, point }) => {
        const key = portKey(source.id, port);
        const from = outputPoint(source, nodeHeight(source, sizes));
        const text = PORT_WORD[port];
        return (
          <Fragment key={key}>
            {text ? <BranchLabel point={labelPoint(routePoints(from, point))} text={text} /> : null}
            <span
              role="presentation"
              data-workflow-port={key}
              data-snapped={snapKey === key || undefined}
              style={position(point)}
              onPointerDown={(event) => onEndpointPointerDown(source.id, port, event)}
              className="absolute [top:var(--point-y)] [left:var(--point-x)] flex size-3 -translate-x-1/2 -translate-y-1/2 cursor-crosshair items-center justify-center rounded-full bg-endpoint text-black shadow-[0_0_0_3px_var(--endpoint-ring),0_0_4px_3px_var(--endpoint-shade)] transition-[box-shadow] duration-150 ease-power3-in-out before:absolute before:-inset-2 before:rounded-full hover:shadow-[0_0_0_5px_var(--endpoint-ring),0_0_4px_5px_var(--endpoint-shade)] data-snapped:shadow-[0_0_0_7px_var(--endpoint-ring),0_0_4px_7px_var(--endpoint-shade)]"
            >
              <ChevronDownSmallIcon className="size-2.5" />
            </span>
          </Fragment>
        );
      })}

      {selected && editable && !insertOptions ? (
        <div
          style={position(actionPoint(selected.points))}
          onPointerDown={(event) => event.stopPropagation()}
          className="absolute z-20 [top:var(--point-y)] [left:var(--point-x)] -translate-x-1/2 -translate-y-1/2 transition-[opacity,scale] duration-150 ease-power3-out starting:scale-90 starting:opacity-0"
        >
          <Button variant="destructive" size="xs" onClick={onDeleteEdge} className="shadow-canvas-popover" data-workflow-edge-delete="">
            <Trash2Icon />
            Delete link
          </Button>
        </div>
      ) : null}

      {/* While a step is dragged from the panel: a marker on every link; only a drop on one puts the step in between. */}
      {insertOptions
        ? routed.map(({ edge, source, target, points }) => {
            const active = edge.id === insertEdgeId;
            return (
              <div
                key={`insert-${edge.id}`}
                aria-hidden
                style={position(actionPoint(points))}
                data-workflow-insert={edge.id}
                data-active={active || undefined}
                className="pointer-events-none absolute z-20 [top:var(--point-y)] [left:var(--point-x)] -translate-x-3 -translate-y-1/2"
              >
                <span
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full border border-dashed border-primary/60 bg-canvas-panel text-primary transition-[scale,background-color,border-color] duration-150 ease-power3-out starting:scale-50 starting:opacity-0",
                    active && "scale-125 border-solid border-primary bg-primary text-primary-foreground shadow-canvas-popover",
                  )}
                >
                  <PlusIcon className="size-3.5" />
                </span>
                {/* Left of the marker: the step being dragged hangs to the right of the pointer. */}
                {active ? (
                  <span className="absolute top-1/2 right-full mr-3 -translate-y-1/2 rounded-full bg-primary px-2.5 py-1 text-[12px] leading-4 font-medium whitespace-nowrap text-primary-foreground shadow-canvas-popover">
                    Insert between {source.label} and {target.label}
                  </span>
                ) : null}
              </div>
            );
          })
        : null}
    </>
  );
});
